import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Gift, History, ArrowUpFromLine, RefreshCw, Coins } from "lucide-react";
import { toast } from "sonner";
import { formatNumber } from "@/lib/web3";
import { useAuth } from "@/contexts/AuthContext";

const GOLD = "#FFD36A";
const BLUE = "#60A5FA";

export default function WalletPage() {
  const { refreshUser } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hist, setHist] = useState({ withdrawals: [], deposits: [] });
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("withdraw"); // withdraw | reinvest
  const [busy, setBusy] = useState(false);
  // Min limits — mirrored from backend /wallet/withdraw-config (kept here as fallback)
  const MIN_WITHDRAW = 5;
  const MIN_REINVEST = 5;

  const load = async () => {
    try {
      const [s, h] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/wallet/history"),
      ]);
      setSummary(s.data);
      setHist(h.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const claim = async () => {
    try {
      const r = await api.post("/wallet/claim-network-reward");
      toast.success(`Claimed $${formatNumber(r.data.claimed)} network reward`);
      await load(); await refreshUser();
    } catch (e) { toast.error(e?.response?.data?.detail || "Claim failed"); }
  };

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    const min = mode === "withdraw" ? MIN_WITHDRAW : MIN_REINVEST;
    if (amt < min) {
      return toast.error(
        mode === "withdraw"
          ? `Minimum withdrawal is $${min}`
          : `Minimum recompound is $${min}`
      );
    }
    if (amt > availableBalance + 1e-6) {
      return toast.error(`Insufficient wallet balance ($${formatNumber(availableBalance, { minimumFractionDigits: 2 })})`);
    }
    setBusy(true);
    try {
      const endpoint = mode === "withdraw" ? "/wallet/withdraw" : "/wallet/reinvest";
      const r = await api.post(endpoint, { amount: amt });
      if (mode === "withdraw") {
        const net = r?.data?.net_amount ?? amt * 0.9;
        const fee = r?.data?.fee ?? amt * 0.1;
        toast.success(`Withdrawal requested · $${formatNumber(net, { minimumFractionDigits: 2 })} (fee $${formatNumber(fee, { minimumFractionDigits: 2 })})`, {
          description: r?.data?.message,
        });
      } else {
        toast.success(`Recompounded $${formatNumber(amt, { minimumFractionDigits: 2 })}`, {
          description: r?.data?.message || `New $${formatNumber(r?.data?.cap_amount ?? amt * 2, { minimumFractionDigits: 0 })} cap stake card activated`,
        });
      }
      setAmount(""); await load(); await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 bg-white/5" />
        <Skeleton className="h-48 bg-white/5" />
      </div>
    );
  }

  const bonusBal = Number(summary?.daily_bonus_income || 0);
  // Total available reward wallet balance — drained in this order: network_reward_pending → claimable_reward → daily_bonus_income.
  // This MUST match backend `_wallet_balance()` so user sees the same number.
  const availableBalance =
    Number(summary?.network_reward || summary?.network_reward_pending || 0) +
    Number(summary?.claimable_reward || 0) +
    bonusBal;
  const pct = amount && availableBalance > 0 ? Math.min(100, (parseFloat(amount) / availableBalance) * 100) : 0;
  const setMax = () => setAmount(availableBalance > 0 ? availableBalance.toFixed(2) : "");
  const setQuick = (pctOf) => setAmount((availableBalance * pctOf).toFixed(2));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold">Wallet</h1>
        <p className="text-xs text-[var(--text-3)] mt-1">Withdraw or reinvest your earnings.</p>
      </div>

      {/* Claim Network Reward */}
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-3)] px-1">Claim Network Reward</div>
      <div className="card-3d glow-blue lift-on-hover p-5 flex items-center justify-between gap-3 relative" data-testid="wallet-network-reward-card">
        <div className="shimmer-line" />
        <div className="absolute -bottom-6 right-2 coin-3d-wrap opacity-80 pointer-events-none">
          <div className="coin-3d w-20 h-20 rounded-full" style={{
            background: "radial-gradient(circle at 35% 30%, #DBEAFE 0%, #3B82F6 45%, #0B2E66 100%)",
            boxShadow: "inset -5px -7px 18px rgba(0,0,0,0.45), inset 6px 6px 16px rgba(255,255,255,0.30), 0 10px 22px rgba(59,130,246,0.45)",
          }} />
        </div>
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)]">Network Reward</div>
          <div className="mono text-2xl font-semibold" style={{ color: BLUE, textShadow: "0 0 24px rgba(96,165,250,0.4)" }}>${formatNumber(summary?.network_reward, { minimumFractionDigits: 2 })}</div>
        </div>
        <Button data-testid="wallet-claim-button" onClick={claim} className="font-semibold text-black h-10 px-4" style={{ background: "linear-gradient(135deg, #FFD36A 0%, #F3BA2F 100%)" }}>
          <Gift size={16} className="mr-2" /> Claim
        </Button>
      </div>

      {/* 2 income cards - dynamic gradient */}
      <div className="grid grid-cols-2 gap-3">
        <div className="card-3d lift-on-hover kpi-gold p-4 relative" data-testid="wallet-bonus-income-card">
          <div className="shimmer-line" />
          <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)] mb-1">Daily Bonus Income</div>
          <div className="mono text-xl font-semibold" style={{ color: GOLD, textShadow: "0 0 18px rgba(255,211,106,0.4)" }}>${formatNumber(summary?.daily_bonus_income, { minimumFractionDigits: 2 })}</div>
          <div className="text-[10px] text-[var(--text-3)] mt-1">Total <span className="mono text-[var(--text-2)]">${formatNumber(summary?.daily_bonus_income, { minimumFractionDigits: 2 })}</span></div>
        </div>
        <div className="card-3d lift-on-hover kpi-blue p-4 relative" data-testid="wallet-network-card">
          <div className="shimmer-line" />
          <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)] mb-1">Network Reward</div>
          <div className="mono text-xl font-semibold" style={{ color: BLUE, textShadow: "0 0 18px rgba(96,165,250,0.4)" }}>${formatNumber(summary?.network_reward, { minimumFractionDigits: 2 })}</div>
          <div className="text-[10px] text-[var(--text-3)] mt-1">Total <span className="mono text-[var(--text-2)]">${formatNumber(summary?.network_reward, { minimumFractionDigits: 2 })}</span></div>
        </div>
      </div>

      {/* From Bonus Income action */}
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-3)] px-1 flex items-center justify-between">
        <span>From Wallet Balance</span>
        <span className="mono text-[var(--text-2)] normal-case tracking-normal">
          Available: <span style={{ color: GOLD }}>${formatNumber(availableBalance, { minimumFractionDigits: 2 })}</span>
        </span>
      </div>
      <div className="card-3d lift-on-hover p-5" data-testid="wallet-action-card">
        <Tabs value={mode} onValueChange={(v) => { setMode(v); setAmount(""); }}>
          <TabsList className="tabs-3d w-full grid grid-cols-2 gap-1 h-auto">
            <TabsTrigger value="withdraw" data-testid="wallet-tab-withdraw" className="h-10 font-semibold"><ArrowUpFromLine size={14} className="mr-1.5" /> Withdraw</TabsTrigger>
            <TabsTrigger value="reinvest" data-testid="wallet-tab-reinvest" className="h-10 font-semibold"><RefreshCw size={14} className="mr-1.5" /> Recompound</TabsTrigger>
          </TabsList>
          <TabsContent value={mode} className="mt-4 space-y-3">
            <div className="text-center">
              <div className="mono text-3xl font-semibold" style={{ color: GOLD }}>$ {formatNumber(amount || 0, { minimumFractionDigits: 2 })}</div>
              <div className="text-[11px] text-[var(--text-3)] mt-1">
                {pct.toFixed(0)}% of balance · Min ${mode === "withdraw" ? MIN_WITHDRAW : MIN_REINVEST}
              </div>
            </div>
            <Input
              data-testid="wallet-amount-input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`Min $${mode === "withdraw" ? MIN_WITHDRAW : MIN_REINVEST} USDT`}
              type="number"
              step="0.01"
              min={mode === "withdraw" ? MIN_WITHDRAW : MIN_REINVEST}
              className="bg-[var(--surface-1)] border-white/10 mono h-11 text-center"
            />

            {/* Quick amount chips */}
            <div className="grid grid-cols-4 gap-1.5">
              {[0.25, 0.5, 0.75, 1].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setQuick(p)}
                  disabled={availableBalance <= 0}
                  data-testid={`wallet-quick-${Math.round(p*100)}`}
                  className="h-8 rounded-lg text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors disabled:opacity-40"
                  style={{
                    background: "rgba(255,211,106,0.06)",
                    border: "1px solid rgba(255,211,106,0.20)",
                    color: "#FFD36A",
                  }}
                >
                  {p === 1 ? "MAX" : `${Math.round(p * 100)}%`}
                </button>
              ))}
            </div>

            {/* Recompound info — only for Recompound tab */}
            {mode === "reinvest" && parseFloat(amount) >= MIN_REINVEST && (
              <div
                className="relative p-3 rounded-xl overflow-hidden"
                style={{
                  background: "linear-gradient(160deg, rgba(34,197,94,0.10) 0%, var(--surface-1) 60%, var(--surface-2) 100%)",
                  border: "1.5px solid rgba(34,197,94,0.30)",
                }}
                data-testid="reinvest-info-card"
              >
                <div className="relative space-y-1.5 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text-3)] uppercase tracking-[0.10em] text-[10px]">Recompound Amount</span>
                    <span className="mono font-semibold">${formatNumber(parseFloat(amount) || 0, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text-3)] uppercase tracking-[0.10em] text-[10px]">New 2X Cap Card</span>
                    <span className="mono font-semibold" style={{ color: "#86EFAC" }}>
                      ${formatNumber((parseFloat(amount) || 0) * 2, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text-3)] uppercase tracking-[0.10em] text-[10px]">Daily ROI (1%)</span>
                    <span className="mono font-semibold" style={{ color: "#FFD36A" }}>
                      ${formatNumber((parseFloat(amount) || 0) * 0.01, { minimumFractionDigits: 2 })}/day
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Withdrawal fee breakdown - only for Withdraw tab */}
            {mode === "withdraw" && (
              <div
                className="relative p-3 rounded-xl overflow-hidden"
                style={{
                  background: "linear-gradient(160deg, rgba(255,211,106,0.10) 0%, var(--surface-1) 60%, var(--surface-2) 100%)",
                  border: "1.5px solid rgba(255,211,106,0.30)",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08), 0 4px 12px -4px rgba(255,211,106,0.25)",
                }}
                data-testid="withdraw-fee-breakdown"
              >
                <div className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                  style={{ background: "linear-gradient(180deg, rgba(255,211,106,0.06) 0%, transparent 100%)" }} />
                <div className="relative space-y-1.5 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text-3)] uppercase tracking-[0.10em] text-[10px]">Withdraw Amount</span>
                    <span className="mono font-semibold text-[var(--text-1)]">${formatNumber(parseFloat(amount) || 0, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text-3)] uppercase tracking-[0.10em] text-[10px]">Fee (10%)</span>
                    <span className="mono font-semibold" style={{ color: "#F87171" }} data-testid="withdraw-fee-value">
                      − ${formatNumber(((parseFloat(amount) || 0) * 0.10), { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="h-px my-1" style={{ background: "rgba(255,211,106,0.20)" }} />
                  <div className="flex items-center justify-between">
                    <span className="font-semibold uppercase tracking-[0.10em] text-[10px]" style={{ color: "#FFD36A" }}>You will receive</span>
                    <span className="mono text-base font-bold" style={{ color: "#FFD36A", textShadow: "0 0 8px rgba(255,211,106,0.4)" }} data-testid="withdraw-net-value">
                      ${formatNumber(((parseFloat(amount) || 0) * 0.90), { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <Button data-testid="wallet-action-submit" onClick={submit} disabled={busy} className="w-full h-11 font-semibold text-black" style={{ background: "linear-gradient(135deg, #FFD36A 0%, #F3BA2F 100%)" }}>
              {busy ? "Processing..." : (mode === "withdraw" ? "Withdraw" : "Recompound")}
            </Button>
          </TabsContent>
        </Tabs>
      </div>

      {/* History */}
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-3)] px-1 flex items-center gap-2"><History size={12} /> Recent Activity</div>
      <div className="card-3d lift-on-hover p-2" data-testid="wallet-history-card">
        {(hist.withdrawals.length === 0 && hist.deposits.length === 0) ? (
          <div className="text-center text-sm text-[var(--text-3)] py-8">No history yet.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {[...hist.deposits.map(d => ({...d, kind: "deposit"})), ...hist.withdrawals.map(d => ({...d, kind: "withdraw"}))]
              .sort((a,b) => String(b.created_at).localeCompare(String(a.created_at)))
              .slice(0, 10)
              .map((it, i) => {
                const st = String(it.status || "").toLowerCase();
                let statusLabel = null, statusBg = null, statusFg = null;
                if (it.kind === "withdraw") {
                  if (st === "pending") {
                    statusLabel = "PENDING";
                    statusBg = "rgba(255,211,106,0.14)";
                    statusFg = "#FFD36A";
                  } else if (st === "completed" || st === "paid" || st === "approved") {
                    statusLabel = "COMPLETED";
                    statusBg = "rgba(34,197,94,0.14)";
                    statusFg = "#86EFAC";
                  } else if (st === "rejected" || st === "failed") {
                    statusLabel = "REJECTED";
                    statusBg = "rgba(248,113,113,0.14)";
                    statusFg = "#FCA5A5";
                  }
                }
                return (
                  <div key={i} className="flex items-center justify-between px-3 py-3" data-testid={`history-row-${it.kind}-${i}`}>
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: it.kind === "deposit" ? "rgba(34,197,94,0.16)" : "rgba(255,211,106,0.16)", color: it.kind === "deposit" ? "#86EFAC" : GOLD }}>
                        {it.kind === "deposit" ? <Coins size={14} /> : <ArrowUpFromLine size={14} />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-medium capitalize">{it.kind} {it.package_name ? `· ${it.package_name}` : ""}</span>
                          {statusLabel && (
                            <span
                              className="text-[9px] font-bold tracking-[0.10em] px-1.5 py-[2px] rounded-md"
                              style={{ background: statusBg, color: statusFg, border: `1px solid ${statusFg}33` }}
                              data-testid={`history-status-${st}`}
                            >
                              {statusLabel}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-[var(--text-3)] mono mt-0.5">{new Date(it.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="mono text-sm shrink-0" style={{ color: it.kind === "deposit" ? "#86EFAC" : (st === "rejected" ? "#FCA5A5" : GOLD) }}>${formatNumber(it.amount, { minimumFractionDigits: 2 })}</div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}
